/** Jeder Clip im Schnitt, mit der Spur, auf der er liegt. */
export function alleClips(timeline) {
    return timeline.tracks.flatMap((t) => t.clips.map((clip) => ({ clip, trackId: t.id })));
}
/**
 * Die ganze Gruppe zu einem Clip -- er selbst eingeschlossen.
 *
 * Gehört er zu keiner, steht er allein darin. Das ist bewusst so: der
 * Aufrufer muss dann nicht zweimal unterscheiden.
 */
export function gruppeVon(timeline, clipId) {
    const alle = alleClips(timeline);
    const eigener = alle.find((e) => e.clip.id === clipId);
    if (!eigener)
        return [];
    if (!eigener.clip.gruppe)
        return [{ clipId, trackId: eigener.trackId }];
    return alle
        .filter((e) => e.clip.gruppe === eigener.clip.gruppe)
        .map((e) => ({ clipId: e.clip.id, trackId: e.trackId }));
}
/**
 * Eine Auswahl auf ganze Gruppen erweitern.
 *
 * Wer einen Clip antippt, meint die Gruppe -- sonst wäre „bewegen sich
 * zusammen“ nicht zu haben. Die Reihenfolge der ursprünglichen Auswahl bleibt
 * vorn, damit „der erste Gewählte“ derselbe bleibt.
 */
export function mitGruppe(timeline, ids) {
    const heraus = [];
    const drin = new Set();
    for (const id of ids) {
        // Der Angetippte zuerst, dann der Rest seiner Gruppe: `gruppeVon` gibt sie
        // in der Reihenfolge des Schnitts zurück, und dann stünde ein anderer
        // vorn als der, den jemand angefasst hat.
        const gefunden = gruppeVon(timeline, id);
        // Und wer hier nicht gefunden wird, bleibt trotzdem stehen. **Erweitern
        // darf niemals verkleinern.** Beim Bauen hat genau das die Auswahl auf der
        // Leinwand verschluckt: dort wird gewählt, bevor der Schnitt im Speicher
        // steht, `gruppeVon` fand nichts, und die Auswahl kam leer zurück.
        const gruppe = gefunden.length > 0 ? gefunden : [{ clipId: id, trackId: '' }];
        for (const ort of [...gruppe.filter((o) => o.clipId === id), ...gruppe.filter((o) => o.clipId !== id)]) {
            if (drin.has(ort.clipId))
                continue;
            drin.add(ort.clipId);
            heraus.push(ort.clipId);
        }
    }
    return heraus;
}
/** Steht diese Auswahl für genau eine vollständige Gruppe? */
export function istEineGruppe(timeline, ids) {
    if (ids.length < 2)
        return false;
    const alle = alleClips(timeline);
    const gruppen = new Set(ids.map((id) => alle.find((e) => e.clip.id === id)?.clip.gruppe ?? null));
    return gruppen.size === 1 && !gruppen.has(null);
}
/**
 * Wohin die Begleiter wandern, wenn der angefasste Clip von `von` nach `nach`
 * zieht.
 *
 * Sie wandern **um denselben Betrag** und bleiben auf ihrer eigenen Spur: eine
 * Gruppe ist ein zeitlicher Zusammenhalt, kein räumlicher. Zöge der Ton mit
 * auf die Bildspur, wäre die Gruppe nach einem Zug kaputt.
 *
 * Landet auch nur einer von ihnen auf einem fremden Clip, gibt es `null`
 * zurück -- die ganze Bewegung gilt dann als „kein Platz“. Einen Teil der
 * Gruppe zu bewegen und den Rest stehen zu lassen, wäre genau das, was die
 * Gruppe verhindern soll.
 */
export function gruppenZug(timeline, ankerId, von, nach) {
    const um = nach - von;
    const alle = alleClips(timeline);
    const gruppe = gruppeVon(timeline, ankerId).filter((o) => o.clipId !== ankerId);
    const zuege = [];
    for (const ort of gruppe) {
        const eintrag = alle.find((e) => e.clip.id === ort.clipId);
        if (!eintrag)
            continue;
        const start = eintrag.clip.start + um;
        if (start < 0)
            return null;
        const spur = timeline.tracks.find((t) => t.id === ort.trackId);
        const belegt = (spur?.clips ?? []).some((c) => {
            // Die Gruppe selbst zählt nicht als Hindernis: sie zieht ja mit.
            if (gruppe.some((g) => g.clipId === c.id) || c.id === ankerId)
                return false;
            return start < c.start + c.duration - 1e-9 && start + eintrag.clip.duration > c.start + 1e-9;
        });
        if (belegt)
            return null;
        zuege.push({ clipId: ort.clipId, start });
    }
    return zuege;
}
/**
 * **Eine Auswahl zieht gemeinsam** (7.205; Sascha, 4. Oktober: mehrere
 * Clips am Desktop markieren und gemeinsam schieben). Alle gewählten Clips
 * -- und wer mit ihnen gruppiert ist (S3) -- wandern um denselben Betrag,
 * jeder auf seiner Spur. Wie bei der Gruppe: geht es für einen nicht (vor
 * 0, oder dort liegt ein Clip, der nicht mitzieht), geht es für keinen.
 */
export function auswahlZug(timeline, ids, um) {
    const ziehen = new Set(mitGruppe(timeline, ids));
    const zuege = [];
    for (const { clip, trackId } of alleClips(timeline)) {
        if (!ziehen.has(clip.id))
            continue;
        const start = clip.start + um;
        if (start < -1e-9)
            return null;
        const spur = timeline.tracks.find((t) => t.id === trackId);
        const belegt = (spur?.clips ?? []).some((c) => !ziehen.has(c.id) && start < c.start + c.duration - 1e-9 && start + clip.duration > c.start + 1e-9);
        if (belegt)
            return null;
        zuege.push({ clipId: clip.id, trackId, start: Math.max(0, start) });
    }
    return zuege;
}
//# sourceMappingURL=gruppen.js.map